/**
 * PDP Main Buy Box v2 — state management for the Figma-redesigned buy box.
 *
 * State: { days, planId, qty, isSub }
 * Price math (all in pennies until display):
 *   base = data-base-price-cents  (e.g. 3499 for £34.99)
 *   subFactor   = isSub ? (1 - subDiscount/100) : 1          // Loop plan discount
 *   bundleFactor= qty>=4 ? (1 - b4/100) : qty>=3 ? (1 - b3/100) : qty>=2 ? (1 - b2/100) : 1
 *   tubsPrice   = ceil(ceil(base * subF) * bundleF) * qty    // two-step ceil
 *   perDay      = tubsPrice / (days * qty)
 *
 * Each buybox-v2 root is initialised independently so multiple on a page is safe.
 */
(function () {
  const roots = document.querySelectorAll('.buybox-v2');
  roots.forEach(init);

  function init(root) {
    if (root.dataset.initialised === '1') return;
    root.dataset.initialised = '1';

    const basePrice = Number(root.dataset.basePriceCents) || 0;
    const subDiscount = Number(root.dataset.subDiscount) || 0;
    const dentalCents = Number(root.dataset.dentalCents) || 0;
    const shippingCents = Number(root.dataset.shippingCents) || 0;
    const bundleTiers = {
      2: Number(root.dataset.bundle2) || 0,
      3: Number(root.dataset.bundle3) || 0,
      4: Number(root.dataset.bundle4) || 0,
    };
    const moneyFormat = root.dataset.moneyFormat || '£{{amount}}';

    const weightBtns = root.querySelectorAll('.buybox-v2__weight');
    const firstWeight = weightBtns[0];
    const state = {
      days: Number(firstWeight?.dataset.days) || 120,
      planId: firstWeight?.dataset.planId || '',
      qty: 1,
      isSub: true,
    };

    // --- Weight selection --------------------------------------------------
    weightBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        weightBtns.forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-checked', 'true');
        state.days = Number(btn.dataset.days) || state.days;
        state.planId = btn.dataset.planId || '';
        // For larger dogs (≤45 days per tub), default to 2 tubs — smaller
        // dogs (60 / 120 day supply) stay on 1 tub.
        state.qty = state.days <= 45 ? 2 : 1;
        render();
      });
    });

    // --- Subscription toggle ----------------------------------------------
    const subToggle = root.querySelector('[data-role="sub-toggle"]');
    subToggle?.addEventListener('change', (e) => {
      state.isSub = e.target.checked;
      render();
    });

    // --- Upsell qty buttons (legacy support) ------------------------------
    root.querySelectorAll('.buybox-v2__upsell-card, .buybox-v2__upsell-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        state.qty = Number(btn.dataset.qty) || 1;
        render();
      });
    });

    // --- Multi-tub pills --------------------------------------------------
    const tubPills = root.querySelectorAll('.buybox-v2__tub-pill');
    tubPills.forEach((btn) => {
      btn.addEventListener('click', () => {
        state.qty = Number(btn.dataset.qty) || 1;
        render();
      });
    });

    // --- Form submit: ensure qty + selling_plan inputs are fresh ----------
    const form = root.querySelector('[data-role="cart-form"]');
    form?.addEventListener('submit', () => {
      const qtyInput = form.querySelector('[data-role="qty-input"]');
      const planInput = form.querySelector('[data-role="plan-input"]');
      if (qtyInput) qtyInput.value = String(state.qty);
      if (planInput) planInput.value = state.isSub && state.planId ? state.planId : '';
    });

    // --- Helpers ----------------------------------------------------------
    function money(cents) {
      const amount = (cents / 100).toFixed(2);
      return moneyFormat
        .replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
    }
    function bundleFactor(qty) {
      if (qty >= 4) return 1 - bundleTiers[4] / 100;
      if (qty >= 3) return 1 - bundleTiers[3] / 100;
      if (qty >= 2) return 1 - bundleTiers[2] / 100;
      return 1;
    }
    function setText(sel, val) {
      root.querySelectorAll(`[data-role="${sel}"]`).forEach((el) => { el.textContent = val; });
    }

    // --- Render -----------------------------------------------------------
    function render() {
      const { days, qty, isSub } = state;
      const subF = isSub ? 1 - subDiscount / 100 : 1;
      const bundleF = bundleFactor(qty);

      // Two-step ceiling: ceil sub price, then ceil after bundle.
      const subTubCents = Math.ceil(basePrice * subF);
      const perTubPriceCents = Math.ceil(subTubCents * bundleF);
      const tubsPriceCents = perTubPriceCents * qty;
      const tubsCompareCents = basePrice * qty;
      const totalDays = days * qty;
      const perDayCents = totalDays > 0 ? tubsPriceCents / totalDays : 0;

      // Totals
      setText('total-days', String(totalDays));
      setText('includes-days', String(totalDays));
      setText('tub-count', String(qty));
      setText('tubs-days', String(totalDays));
      setText('upsell-2-days', String(days * 2));
      setText('upsell-3-days', String(days * 3));

      // Tub plural
      setText('tub-plural', qty > 1 ? 's' : '');

      // Plan / Trial word (swaps "Protocol" when sub on, "Trial" when OTP)
      setText('plan-word', isSub ? 'Protocol' : 'Trial');

      // Multi-tub pill labels — each shows total days for that qty tier
      for (let n = 1; n <= 4; n++) {
        setText(`tub-pill-${n}-days`, String(days * n));
      }

      // "Recommended" badge on 2-tub pill — shows only for larger dogs (days ≤ 45).
      const tubRec = root.querySelector('[data-role="tub-pill-2-recommended"]');
      if (tubRec) tubRec.hidden = days > 45;

      // "9/10 owners see results in this time" callout visibility:
      //   small dogs (days ≥ 60)  → always show, every qty (their 1-tub plan
      //                             is already long enough for results)
      //   larger dogs (days ≤ 45) → only show on qty ≥ 2 (1 tub is too short)
      const callout = root.querySelector('[data-role="tubs-callout"]');
      if (callout) {
        const showForSmallDog = days >= 60;
        const showForLargeDog = days <= 45 && qty >= 2;
        callout.hidden = !(showForSmallDog || showForLargeDog);
      }

      // Guarantee (sub = 365, OTP = 90)
      setText('guarantee-days', isSub ? '365' : '90');
      setText('guarantee-days-2', isSub ? '365' : '90');

      // Prices
      setText('tubs-price', money(tubsPriceCents));
      setText('price-per-day', money(Math.round(perDayCents)));
      setText('tubs-perday', money(Math.round(perDayCents)));
      setText('atc-price', money(tubsPriceCents));

      // Strike-through only when sub OR bundle discount applied
      const showCompare = isSub || bundleF < 1;
      root.querySelectorAll('[data-role="tubs-compare"]').forEach((el) => {
        el.textContent = money(tubsCompareCents);
        el.style.display = showCompare ? '' : 'none';
      });

      // Sub-only rows
      root.querySelectorAll('[data-sub-only]').forEach((el) => {
        el.style.display = isSub ? '' : 'none';
      });

      // OTP-only nudge
      root.querySelectorAll('[data-otp-only]').forEach((el) => {
        el.hidden = isSub;
      });

      // Best Value badge sub-only
      const bestValue = root.querySelector('[data-role="best-value"]');
      if (bestValue) bestValue.style.display = isSub ? '' : 'none';

      // Owners divider (1,012+ sold pill) sub-only
      const ownersDivider = root.querySelector('[data-role="owners-divider"]');
      if (ownersDivider) ownersDivider.style.display = isSub ? '' : 'none';

      // Plan-length pills always visible
      const tubs = root.querySelector('[data-role="tubs"]');
      if (tubs) tubs.hidden = false;

      // Sync pill active states
      tubPills.forEach((p) => {
        const active = Number(p.dataset.qty) === qty;
        p.classList.toggle('is-active', active);
      });

      // Sub label swap (driven by data-sub-on / data-sub-off)
      const subLabel = root.querySelector('[data-role="sub-label"]');
      if (subLabel && (subLabel.dataset.subOn || subLabel.dataset.subOff)) {
        subLabel.textContent = isSub
          ? (subLabel.dataset.subOn || subLabel.textContent)
          : (subLabel.dataset.subOff || subLabel.textContent);
      }

      // Toggle root-level state class for CSS-driven OFF-state styling
      root.classList.toggle('is-sub-off', !isSub);
    }

    render();
  }
})();